import React, { useState, useEffect, useCallback } from 'react';
import { Grid, GridItem, Column, Row, Button, TextLink, LoadingSpinner, ErrorBox } from '@mattilsynet/mt-ui';
import { useTypedSelector } from '../../common/custom-hooks';
import { IStoreState } from '../../reducers/types';
import './style.css';
import { kvitteringStatusEnum } from '../../ducks/kvittering/types';
import { useMediaQuery } from 'react-responsive';
import { copySaknummer, offlineInFerdigstillingProcess, useSingleToast } from '../../common/toast';
import { ModalWrapper } from '../../components/modal-wrapper';
import FerdigstillingOkContent from '../../components/ferdigstilling-ok-content';
import { CopyToClipboardButton } from '../../components/copy-to-clipboard';
import { ModalButtonsContainer } from '../../components/modal-buttons-container';
import { ModalButton } from '../../components/modal-button';
import { modalContentPaddingLarge } from '../../components/modal-buttons-container/constants';
import { useParams } from 'react-router';
import { useTKNavigate } from '../../common/navigation';
import { useArkiveringStatus } from '../../features/arkivering-status';
import { useFerdigstillKvittering, useKvittering } from '../../features/kvitteringer';
import { KvitteringQueryStatusEnum } from '../../features/kvitteringer/types';
export const getFerdigstillingRoute = (kvitteringId: string) => `/ferdigstilling/${kvitteringId}`;
const FerdistillingRoute = () => {
  const {
    id: kvitteringId
  } = useParams();
  const {
    data: currentKvittering,
    isFetched: hasFetchedKvittering,
    isPending: isKvitteringLoading,
    error: kvitteringError,
    refetch: refetchKvittering
  } = useKvittering(kvitteringId);
  const {
    data: arkiveringStatus,
    isFetched: hasFetchedArchiveStatus,
    isPending: isArkiveringStatusLoading,
    error: arkiveringError
  } = useArkiveringStatus(kvitteringId ?? '');
  const {
    mutate: ferdigstillKvittering,
    status: ferdigstillingStatus,
    isPending: isFerdigstilling
  } = useFerdigstillKvittering(currentKvittering);
  const navigate = useTKNavigate();
  const singleToast = useSingleToast();
  const combinedArkiveringStatus = arkiveringStatus?.reduce((acc, cur) => ({
    antallBilder: acc.antallBilder + cur.antallBilder,
    antallBilderArkivert: acc.antallBilderArkivert + cur.antallBilderArkivert,
    numPending: acc.numPending + (cur.status === 'PENDING' ? 1 : 0),
    numError: acc.numError + (cur.status === 'ERROR' ? 1 : 0),
    numArchived: acc.numArchived + (cur.status === 'ARCHIVED' ? 1 : 0)
  }), {
    antallBilder: 0,
    antallBilderArkivert: 0,
    numArchived: 0,
    numPending: 0,
    numError: 0
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isOffline = useTypedSelector((state: IStoreState) => state.ui.offline);
  useEffect(() => {
    if (!kvitteringId) {
      navigate('/');
    }
  }, [kvitteringId, navigate]);
  const {
    noarksakAar,
    noarksakSekvensnummer,
    hoveddokumentId,
    virksomhetsNavn
  } = currentKvittering || {};
  const saksnummer = `${noarksakAar}/${noarksakSekvensnummer}`;
  const hasHoveddokumentId = !!noarksakAar && !!noarksakSekvensnummer && !!hoveddokumentId;
  const onCopySaknummer = useCallback(() => singleToast.showToast(copySaknummer()), [singleToast]);
  const onCloseModal = (shouldRefreshdata: boolean) => {
    if (shouldRefreshdata) {
      refetchKvittering();
    }
    setIsModalOpen(false);
  };
  const renderImageArchiveStatus = () => {
    if ((combinedArkiveringStatus?.numPending ?? 0) > 0) {
      return <>
          <b>
            Det er bilder på vei til arkivet. Du kan ferdigstille og sende ut
            tilsynskvitteringen når bildene er framme. Det vil ikke ta lang tid.
          </b>
          <p>Last inn siden på nytt for å sjekke om bildene har kommet frem.</p>
          <TextLink onClick={() => {
          refetchKvittering();
        }}>
            Last inn siden på nytt
          </TextLink>
        </>;
    }
    if ((combinedArkiveringStatus?.antallBilderArkivert ?? 0) > 0) {
      return <p>Alle bildene har blitt arkivert.</p>;
    }
    return null;
  };
  const onOpenModal = () => {
    if (isOffline) {
      return singleToast.showToast(offlineInFerdigstillingProcess());
    }
    setIsModalOpen(true);
  };
  const renderContent = () => {
    if ((!hasFetchedKvittering || !hasFetchedArchiveStatus && currentKvittering?.status === kvitteringStatusEnum.ARKIVERT && hasHoveddokumentId || isKvitteringLoading || isArkiveringStatusLoading) && !kvitteringError) {
      return <LoadingSpinner title="Henter arkiveringsstatus..." />;
    }
    if (!hasHoveddokumentId || arkiveringError) {
      return <>
          <b>En feil har oppstått</b>
          <TextLink onClick={() => navigate('/')}>
            Gå tilbake til forsiden
          </TextLink>
        </>;
    }
    if (currentKvittering?.status === kvitteringStatusEnum.FERDIGSTILT && ferdigstillingStatus !== KvitteringQueryStatusEnum.PENDING) {
      return <>
          <b>Kvitteringen er ferdigstilt</b>
          <TextLink onClick={() => navigate('/')}>
            Gå tilbake til forsiden
          </TextLink>
        </>;
    }
    return <>
        <p>
          {`Tilsynskvittering for tilsyn hos ${virksomhetsNavn} har blitt opprettet i arkivet på sak ${saksnummer}`}
          &nbsp;
          <CopyToClipboardButton onCopied={onCopySaknummer} value={saksnummer} data-sentry-element="CopyToClipboardButton" data-sentry-source-file="index.tsx" />
        </p>
        {renderImageArchiveStatus()}
        <div>
          <Row justify="center" margin={[1, 0]} data-sentry-element="Row" data-sentry-source-file="index.tsx">
            <Button large disabled={!!combinedArkiveringStatus?.numPending} onClick={() => onOpenModal()} data-sentry-element="Button" data-sentry-source-file="index.tsx">
              Ferdigstill og send ut tilsynskvitteringen
            </Button>
          </Row>
        </div>
      </>;
  };
  return <>
      <Grid id="ferdigstilling" data-sentry-element="Grid" data-sentry-source-file="index.tsx">
        <GridItem xl={[1, -1]} lg={[1, -1]} md={[1, -1]} sm={[1, -1]} data-sentry-element="GridItem" data-sentry-source-file="index.tsx">
          <Row data-sentry-element="Row" data-sentry-source-file="index.tsx">
            <TextLink leftArrow onClick={() => navigate('/')} data-sentry-element="TextLink" data-sentry-source-file="index.tsx">
              Tilbake
            </TextLink>
          </Row>
        </GridItem>
        <GridItem xl={[3, -3]} lg={[3, -3]} md={[1, -1]} sm={[1, -1]} data-sentry-element="GridItem" data-sentry-source-file="index.tsx">
          <Grid data-sentry-element="Grid" data-sentry-source-file="index.tsx">
            <GridItem xl={[2, -2]} lg={[1, -1]} md={[1, -1]} sm={[1, -1]} data-sentry-element="GridItem" data-sentry-source-file="index.tsx">
              <Column spacing={3} fluid key={1} className="ferdigstilling-content" data-sentry-element="Column" data-sentry-source-file="index.tsx">
                <Row className="main-heading" data-sentry-element="Row" data-sentry-source-file="index.tsx">
                  <h1>Ferdigstilling og utsending av tilsynskvittering</h1>
                </Row>
                <Column spacing={2} fluid data-sentry-element="Column" data-sentry-source-file="index.tsx">
                  {renderContent()}
                </Column>
              </Column>
            </GridItem>
          </Grid>
        </GridItem>
      </Grid>
      <FerdigstillingModal kvitteringId={kvitteringId ?? ''} status={currentKvittering?.status} onCancel={onCloseModal} isOpen={isModalOpen} isOffline={isOffline} saksnummer={saksnummer} ferdigstillingStatus={ferdigstillingStatus} isFerdigstilling={isFerdigstilling} ferdigstillKvittering={ferdigstillKvittering} data-sentry-element="FerdigstillingModal" data-sentry-source-file="index.tsx" />
    </>;
};
interface IArchiveModalProps {
  onCancel: (shouldRefreshData: boolean) => void;
  isOpen: boolean;
  saksnummer: string;
  kvitteringId: string;
  isOffline: boolean;
  status: ITilsynskvitteringRemote.TilsynskvitteringDTO['status'];
  ferdigstillKvittering: () => void;
  ferdigstillingStatus: 'error' | 'idle' | 'pending' | 'success';
  isFerdigstilling: boolean;
}
const FerdigstillingModal = ({
  onCancel,
  isOpen,
  saksnummer,
  kvitteringId,
  isOffline,
  status,
  ferdigstillKvittering,
  ferdigstillingStatus,
  isFerdigstilling
}: IArchiveModalProps) => {
  const navigate = useTKNavigate();
  const singleToast = useSingleToast();
  const onFerdigStilling = useCallback(() => {
    if (isOffline) {
      return singleToast.showToast(offlineInFerdigstillingProcess());
    }
    ferdigstillKvittering();
  }, [isOffline, singleToast, ferdigstillKvittering]);
  const onCloseModal = () => {
    if (ferdigstillingStatus === KvitteringQueryStatusEnum.SUCCESS) {
      navigate('/');
    }
    return onCancel(ferdigstillingStatus === KvitteringQueryStatusEnum.ERROR);
  };
  const marginHorizontal = useMediaQuery({
    query: '(min-width: 767px)'
  }) ? 14 : 5;
  const renderModalContent = () => {
    if (isFerdigstilling) {
      return <LoadingSpinner title="Ferdigstiller og sender ut tilsynskvitteringen..." small />;
    }
    if (ferdigstillingStatus === KvitteringQueryStatusEnum.ERROR) {
      return <ErrorBox errorText="Noe gikk galt. Lukk vinduet og prøv igjen" errorAction={() => onCancel(true)} errorActionText="Lukk vinduet" />;
    }
    if (ferdigstillingStatus === KvitteringQueryStatusEnum.SUCCESS) {
      return <FerdigstillingOkContent kvitteringId={kvitteringId} status={status} saksnummer={saksnummer} />;
    }
    return <>
        <Row margin={[0, marginHorizontal]} data-sentry-element="Row" data-sentry-source-file="index.tsx">
          <p>
            Når du ferdigstiller og sender ut tilsynskvitteringen kan du ikke
            lenger gjøre endringer eller legge til bilder.
          </p>
        </Row>
        <Column spacing={4} data-sentry-element="Column" data-sentry-source-file="index.tsx">
          <Row margin={[0, marginHorizontal]} data-sentry-element="Row" data-sentry-source-file="index.tsx">
            <b>
              Er du sikker på at du vil ferdigstille og sende ut
              tilsynskvitteringen?
            </b>
          </Row>
        </Column>
      </>;
  };
  return <ModalWrapper onCancel={onCloseModal} isOpen={isOpen} fullscreenMobile className="ferdigstilling-modal" data-sentry-element="ModalWrapper" data-sentry-component="FerdigstillingModal" data-sentry-source-file="index.tsx">
      <Column padding={modalContentPaddingLarge} spacing={4} margin={[2, 0]} minHeight="unset" data-sentry-element="Column" data-sentry-source-file="index.tsx">
        {renderModalContent()}
      </Column>
      {ferdigstillingStatus !== KvitteringQueryStatusEnum.SUCCESS && ferdigstillingStatus !== KvitteringQueryStatusEnum.ERROR && ferdigstillingStatus !== KvitteringQueryStatusEnum.PENDING && <ModalButtonsContainer>
            <ModalButton secondary onClick={() => onCancel(false)}>
              Avbryt
            </ModalButton>
            <ModalButton allowMultiLine onClick={onFerdigStilling}>
              Ja, ferdigstill og send ut tilsynskvitteringen
            </ModalButton>
          </ModalButtonsContainer>}
    </ModalWrapper>;
};
export default FerdistillingRoute;